import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://spoon-pos.web.app/api';
const FUNCTIONS_API_URL = 'https://us-central1-spoon-pos.cloudfunctions.net/api/api';
const RESTAURANT_ID = process.env.REACT_APP_RESTAURANT_ID;

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Add auth token to requests if available
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('deliveryAuthToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle response errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// Get authentication token for delivery app to access restaurant APIs
const getAuthToken = async () => {
  try {
    // Get stored token
    let token = localStorage.getItem('deliveryAuthToken');
    
    if (!token) {
      // Use Firebase Functions API to get admin token for the specific restaurant
      const functionsAPI = 'https://us-central1-spoon-pos.cloudfunctions.net/api/api';
      
      const response = await axios.post(`${functionsAPI}/auth/login`, {
        username: 'admin',
        password: 'admin123'
      });
      
      if (response.data.success && response.data.token) {
        token = response.data.token;
        localStorage.setItem('deliveryAuthToken', token);
      } else {
        throw new Error('Failed to get auth token');
      }
    }
    
    return token;
  } catch (error) {
    console.error('Auth error:', error);
    localStorage.removeItem('deliveryAuthToken');
    throw error;
  }
};

// Restaurant services - Using public API endpoints
export const restaurantAPI = {
  getConfig: async () => {
    try {
      console.log(`Getting restaurant config for: ${RESTAURANT_ID}`);
      const response = await axios.get(`${API_URL}/public/restaurant/${RESTAURANT_ID}`);
      
      if (response.data.success) {
        return { 
          success: true, 
          data: response.data.restaurant
        };
      } else {
        throw new Error('Restaurant not found');
      }
    } catch (error) {
      console.error('Error getting restaurant config:', error);
      throw error;
    }
  },

  getMenu: async () => {
    try {
      console.log(`Getting menu for restaurant: ${RESTAURANT_ID}`);
      const response = await axios.get(`${API_URL}/public/restaurant/${RESTAURANT_ID}/menu`);
      
      if (response.data.success) {
        console.log(`Products received for restaurant ${RESTAURANT_ID}: ${response.data.products.length}`);
        
        return { 
          success: true, 
          data: response.data.products.map(product => ({
            id: product.id,
            name: product.name,
            description: product.description || '',
            price: product.price,
            category: product.category_name,
            available: product.is_active && (product.current_quantity > 0 || product.current_quantity === undefined),
            image: product.image_url || null
          }))
        };
      } else {
        throw new Error('Failed to fetch menu');
      }
    } catch (error) {
      console.error('Error getting menu:', error);
      throw error;
    }
  },

  getCategories: async () => {
    try {
      console.log(`Getting categories for restaurant: ${RESTAURANT_ID}`);
      const response = await axios.get(`${API_URL}/public/restaurant/${RESTAURANT_ID}/categories`);
      
      if (response.data.success) {
        console.log(`Categories received for restaurant ${RESTAURANT_ID}: ${response.data.categories.length}`);
        
        return { 
          success: true, 
          data: response.data.categories.map(category => ({
            id: category.id,
            name: category.name
          }))
        };
      } else {
        throw new Error('Failed to fetch categories');
      }
    } catch (error) {
      console.error('Error getting categories:', error);
      throw error;
    }
  }
};

// Order services - Using public API endpoints
export const orderAPI = {
  create: async (orderData) => {
    try {
      console.log(`Creating order for restaurant: ${RESTAURANT_ID}`);
      
      // Transform order data to match backend API format
      const orderPayload = {
        customer_id: orderData.customer_id,
        items: orderData.items.map(item => ({
          product_id: item.id,
          product_name: item.name,
          quantity: item.quantity,
          price: item.price,
          total: item.price * item.quantity
        })),
        customer_name: orderData.customerName,
        customer_phone: orderData.customerPhone,
        customer_email: orderData.customerEmail || '',
        delivery_address: orderData.deliveryAddress,
        subtotal: orderData.subtotal,
        tax_amount: orderData.taxAmount || 0,
        delivery_fee: orderData.deliveryFee || 30,
        total_amount: orderData.totalAmount,
        order_type: 'delivery',
        payment_method: orderData.paymentMethod || 'cash',
        status: 'pending',
        notes: orderData.notes || ''
      };

      const response = await axios.post(`${API_URL}/public/restaurant/${RESTAURANT_ID}/orders`, orderPayload);
      
      if (response.data.success) {
        console.log('Order creation response:', response.data);
        console.log('Order ID received:', response.data.order?.id);
        return { 
          success: true, 
          data: response.data.order
        };
      }
      throw new Error('Failed to create order');
    } catch (error) {
      console.error('Error creating order:', error);
      throw error;
    }
  },

  getStatus: async (orderId) => {
    try {
      console.log(`Getting order status for: ${orderId} in restaurant: ${RESTAURANT_ID}`);
      const response = await axios.get(`${API_URL}/public/restaurant/${RESTAURANT_ID}/orders/${orderId}`);
      
      if (response.data.success) {
        return { 
          success: true, 
          data: response.data.order
        };
      }
      throw new Error('Order not found');
    } catch (error) {
      console.error('Error getting order:', error);
      throw error;
    }
  },

  // For real-time updates, we'll poll the API instead of using Firestore listeners
  listenToOrder: (orderId, callback) => {
    const pollInterval = setInterval(async () => {
      try {
        const result = await orderAPI.getStatus(orderId);
        callback(result.data);
      } catch (error) {
        console.error('Error polling order status:', error);
      }
    }, 5000); // Poll every 5 seconds

    // Return cleanup function
    return () => clearInterval(pollInterval);
  }
};

// Customer services - Using public API endpoints
export const customerAPI = {
  create: async (customerData) => {
    try {
      console.log(`Creating customer for restaurant: ${RESTAURANT_ID}`);
      
      const customerPayload = {
        name: customerData.name,
        phone: customerData.phone,
        email: customerData.email || '',
        address: customerData.address
      };

      const response = await axios.post(`${API_URL}/public/restaurant/${RESTAURANT_ID}/customers`, customerPayload);
      
      if (response.data.success) {
        return { 
          success: true, 
          data: response.data.customer
        };
      }
      throw new Error('Failed to create customer');
    } catch (error) {
      console.error('Error creating customer:', error);
      throw error;
    }
  },

  getByPhone: async (phone) => {
    try {
      console.log(`Searching customer by phone for restaurant: ${RESTAURANT_ID}`);
      const response = await axios.get(`${API_URL}/public/restaurant/${RESTAURANT_ID}/customers/search/${phone}`);
      
      if (response.data.success && response.data.customers.length > 0) {
        // Find customer with exact phone match
        const customer = response.data.customers.find(c => c.phone === phone);
        if (customer) {
          return { 
            success: true, 
            data: customer
          };
        }
      }
      throw new Error('Customer not found');
    } catch (error) {
      console.error('Error getting customer:', error);
      throw error;
    }
  },

  update: async (customerId, customerData) => {
    try {
      // For now, we'll use the create endpoint as fallback
      return await customerAPI.create({ id: customerId, ...customerData });
    } catch (error) {
      console.error('Error updating customer:', error);
      throw error;
    }
  }
};